/**
 * Pfaff / Husqvarna Viking VP3.
 *
 * Big-endian, nested blocks each introduced by a 3-byte tag and a u32 length to the block end:
 *   "%vsm%" 0x00, producer string (UTF-16BE)
 *   00 02 00 file block: settings string, design extents (µm), stitch count, colour count,
 *     one design block:
 *       00 03 00: centre (µm), extents, size, settings, "xxPP", producer string,
 *         colour block count, then per colour:
 *           00 05 00: start position relative to centre (µm, y up), thread (RGB, type, weight,
 *             catalog number, description, brand), block displacement,
 *             00 01 00 stitch block: 0x0A 0xF6 0x00, stitches.
 * Stitches are signed byte pairs (dx, dy, y down) in 0.1 mm; 0x80 0x01 i16 dx i16 dy 0x80 0x02
 * encodes a long displacement, 0x80 0x03 is a trim. VP3 has no jump command: a jump is merged
 * into the displacement of the following stitch (normally right after a trim).
 * Coordinates in headers are micrometres (machine unit × 100).
 *
 * Reference: EmbroidePy/pyembroidery (MIT) Vp3Writer/Vp3Reader; independent implementation.
 * Unlike the reference, block start positions are written without truncation so they round-trip exactly.
 */
import { t } from '../i18n/i18n';
import { CMD } from '../engine/stitch/sequence';
import type { ThreadColor } from '../engine/model/types';
import { ByteReader, ByteWriter, FormatError } from './bytes';
import { type FormatLimits, type MachinePattern, type MachineStitch, extents } from './machine';
import { hexToRgb, rgbToHex } from './threads';

export const VP3_LIMITS: FormatLimits = { maxStitch: 255, maxJump: 3200, maxCoord: 32767 };
const PRODUCER = 'Produced by     Software Ltd';
const enc = new TextEncoder();
const dec = new TextDecoder();

function string16(w: ByteWriter, s: string): void {
  w.u16be(s.length * 2);
  w.utf16be(s);
}

function string8(w: ByteWriter, s: string): void {
  const b = enc.encode(s);
  w.u16be(b.length);
  w.bytes(b);
}

/** Starts a tagged block and returns a function that patches its length. */
function block(w: ByteWriter, tag: [number, number, number]): () => void {
  w.bytes(tag);
  const at = w.pos;
  w.u32be(0);
  return () => {
    const len = w.pos - at - 4;
    w.patch(at, (x) => x.u32be(len));
  };
}

/** Colour blocks: each later block starts with its COLOR_CHANGE command. */
function vp3Blocks(st: MachineStitch[]): MachineStitch[][] {
  const out: MachineStitch[][] = [];
  let last = 0;
  st.forEach((s, i) => {
    if (s.cmd === CMD.COLOR_CHANGE) {
      out.push(st.slice(last, i));
      last = i;
    }
  });
  out.push(st.slice(last));
  return out;
}

export function writeVp3(p: MachinePattern): Uint8Array {
  if (p.stitches.some((s) => s.cmd === CMD.STOP)) throw new FormatError(t('VP3 export does not support stops without a colour change.'));
  const w = new ByteWriter();
  w.ascii('%vsm%').u8(0);
  string16(w, PRODUCER);
  const endFile = block(w, [0, 2, 0]);
  string16(w, '');
  const e = extents(p.stitches);
  w.i32be(e.maxX * 100).i32be(-e.minY * 100).i32be(e.minX * 100).i32be(-e.maxY * 100);
  const blocks = vp3Blocks(p.stitches);
  const ends = p.stitches.filter((s) => s.cmd === CMD.END).length;
  w.u32be(p.stitches.length - ends);
  w.u8(0).u8(blocks.length).u8(12).u8(0);
  w.u8(1); // one design

  const endDesign = block(w, [0, 3, 0]);
  const width = e.maxX - e.minX;
  const height = e.maxY - e.minY;
  const cx = Math.round((e.maxX + e.minX) / 2);
  const cy = Math.round((e.maxY + e.minY) / 2);
  const hw = Math.round(width / 2);
  const hh = Math.round(height / 2);
  w.i32be(cx * 100).i32be(-cy * 100).u8(0).u8(0).u8(0);
  w.i32be(-hw * 100).i32be(hw * 100).i32be(-hh * 100).i32be(hh * 100);
  w.i32be(width * 100).i32be(height * 100);
  string16(w, '');
  w.bytes([0x64, 0x64]).u32be(4096).u32be(0).u32be(0).u32be(4096);
  w.ascii('xxPP').bytes([1, 0]);
  string16(w, PRODUCER);
  w.u16be(blocks.length);

  blocks.forEach((stitches, bi) => {
    const thread: ThreadColor = p.threads[bi] ?? { id: '', name: t('Unknown'), hex: '#000000' };
    const endColor = block(w, [0, 5, 0]);
    const first = bi === 0 || !stitches.length ? { x: 0, y: 0 } : { x: stitches[0].x, y: stitches[0].y };
    const lastS = stitches.length ? stitches[stitches.length - 1] : { x: 0, y: 0 };
    w.i32be((first.x - cx) * 100).i32be(-(first.y - cy) * 100);
    // thread
    w.bytes([1, 0]).u24be(hexToRgb(thread.hex)).bytes([0, 0, 0, 5, 0x28]);
    string8(w, thread.code ?? '');
    string8(w, thread.name || thread.hex);
    string8(w, thread.brand ?? '');
    w.i32be((lastS.x - first.x) * 100).i32be(-(lastS.y - first.y) * 100);
    const endStitches = block(w, [0, 1, 0]);
    w.bytes([0x0a, 0xf6, 0x00]);
    let lx = first.x, ly = first.y;
    for (const s of stitches) {
      if (s.cmd === CMD.END) {
        w.bytes([0x80, 0x03]);
        break;
      }
      if (s.cmd === CMD.TRIM) {
        w.bytes([0x80, 0x03]);
        continue;
      }
      if (s.cmd !== CMD.STITCH) continue; // colour changes delimit blocks; jumps merge into the next stitch
      const dx = s.x - lx;
      const dy = s.y - ly;
      lx = s.x;
      ly = s.y;
      if (dx >= -127 && dx <= 127 && dy >= -127 && dy <= 127) {
        w.u8(dx & 0xff).u8(dy & 0xff);
      } else {
        w.bytes([0x80, 0x01]).i16be(dx).i16be(dy).bytes([0x80, 0x02]);
      }
    }
    endStitches();
    w.u8(0);
    endColor();
  });
  endDesign();
  endFile();
  return w.toBytes();
}

export interface Vp3File {
  extents: { right: number; top: number; left: number; bottom: number };
  stitchCount: number;
  center: { x: number; y: number };
  threads: ThreadColor[];
  /** Absolute positions in machine units (y down). Jumps appear as long stitches. */
  stitches: MachineStitch[];
}

function readString16(r: ByteReader): string {
  return r.utf16be(r.u16be());
}

function readString8(r: ByteReader): string {
  const n = r.u16be();
  const start = r.pos;
  r.skip(n);
  return dec.decode(r.data.subarray(start, start + n));
}

function expectTag(r: ByteReader, tag: number): number {
  const a = r.u8(), b = r.u8(), c = r.u8();
  if (a !== 0 || b !== tag || c !== 0) throw new FormatError(t('Expected VP3 block 00 0{tag} 00 at byte {pos}.', { tag, pos: r.pos - 3 }));
  const len = r.u32be();
  return r.pos + len;
}

export function readVp3(bytes: Uint8Array): Vp3File {
  const r = new ByteReader(bytes);
  if (r.ascii(5) !== '%vsm%') throw new FormatError(t('Not a VP3 file (missing %vsm%).'));
  r.u8();
  readString16(r);
  const fileEnd = expectTag(r, 2);
  if (fileEnd > bytes.length) throw new FormatError(t('The VP3 file block is longer than the file.'));
  readString16(r);
  const ext = { right: r.i32be() / 100, top: 0 - r.i32be() / 100, left: r.i32be() / 100, bottom: 0 - r.i32be() / 100 };
  const stitchCount = r.u32be();
  r.u8();
  r.u8(); // colour count
  r.skip(2);
  const designs = r.u8();
  if (designs !== 1) throw new FormatError(t('VP3 with {n} designs is not supported.', { n: designs }));
  const designEnd = expectTag(r, 3);
  const center = { x: r.i32be() / 100, y: 0 - r.i32be() / 100 };
  r.skip(3 + 16 + 8);
  readString16(r);
  r.skip(2 + 16);
  if (r.ascii(4) !== 'xxPP') throw new FormatError(t('The VP3 design block is missing xxPP.'));
  r.skip(2);
  readString16(r);
  const count = r.u16be();
  const threads: ThreadColor[] = [];
  const stitches: MachineStitch[] = [];
  let x = 0, y = 0;
  for (let b = 0; b < count; b++) {
    const end = expectTag(r, 5);
    const sx = r.i32be() / 100 + center.x;
    const sy = center.y - r.i32be() / 100;
    if (b > 0) stitches.push({ x, y, cmd: CMD.COLOR_CHANGE });
    x = Math.round(sx);
    y = Math.round(sy);
    const colors = r.u8();
    r.u8();
    let rgb = 0;
    for (let k = 0; k < colors; k++) {
      rgb = r.u24be();
      r.u8();
      r.u16be();
    }
    r.u8();
    r.u8();
    const code = readString8(r);
    const name = readString8(r);
    const brand = readString8(r);
    threads.push({ id: `vp3_${b}`, name, hex: rgbToHex(rgb), code, brand });
    r.skip(8);
    const stEnd = expectTag(r, 1);
    const magic = [r.u8(), r.u8(), r.u8()];
    if (magic[0] !== 0x0a || magic[1] !== 0xf6 || magic[2] !== 0) throw new FormatError(t('Invalid VP3 stitch block.'));
    while (r.pos < stEnd) {
      const b0 = r.u8();
      if (r.pos >= stEnd) break;
      const b1 = r.u8();
      if (b0 !== 0x80) {
        x += b0 > 127 ? b0 - 256 : b0;
        y += b1 > 127 ? b1 - 256 : b1;
        stitches.push({ x, y, cmd: CMD.STITCH });
        continue;
      }
      if (b1 === 0x01) {
        x += r.i16be();
        y += r.i16be();
        if (r.u8() !== 0x80 || r.u8() !== 0x02) throw new FormatError(t('Invalid long VP3 displacement.'));
        stitches.push({ x, y, cmd: CMD.STITCH });
      } else if (b1 === 0x03) {
        stitches.push({ x, y, cmd: CMD.TRIM });
      } else if (b1 === 0x02) {
        // no-op marker
      } else {
        throw new FormatError(t('Unknown VP3 control code 0x{code}.', { code: b1.toString(16) }));
      }
    }
    r.seek(end);
  }
  if (r.pos > designEnd) throw new FormatError(t('The VP3 design block is corrupt.'));
  stitches.push({ x, y, cmd: CMD.END });
  return { extents: ext, stitchCount, center, threads, stitches };
}
